import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { CreditCard, Plus, Trash2, Check, Star, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';

// Initialize Stripe with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Card brand icons/colors
const cardBrandStyles = {
  visa: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  mastercard: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  amex: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  discover: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  default: { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200' }
};

// Add Card Form Component
const AddCardForm = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create setup intent
      const headers = getAuthHeaders();
      const { data } = await axios.post(`${API}/payments/save-card`, {}, { 
        withCredentials: true, 
        headers 
      });

      // Confirm the setup intent with card details
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        data.client_secret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
          },
        }
      );

      if (stripeError) {
        setError(stripeError.message);
        toast.error(stripeError.message);
      } else if (setupIntent.status === 'succeeded') {
        toast.success('Card saved successfully!');
        onSuccess();
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to save card';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 border border-slate-200 rounded-xl">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#1e293b',
                '::placeholder': {
                  color: '#94a3b8',
                },
              },
              invalid: {
                color: '#ef4444',
              },
            },
          }}
        />
      </div>
      
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      <div className="flex gap-3 justify-end">
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-full">
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={!stripe || loading}
          className="bg-[#5ebc67] hover:bg-[#4da855] rounded-full"
        >
          {loading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
          ) : (
            <><CreditCard className="w-4 h-4 mr-2" /> Save Card</>
          )}
        </Button>
      </div>
    </form>
  );
};

// Main SavedCards Component
const SavedCardsContent = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCard, setShowAddCard] = useState(false);
  const [deletingCard, setDeletingCard] = useState(null);
  const [settingDefault, setSettingDefault] = useState(null);

  const fetchCards = async () => {
    try {
      const headers = getAuthHeaders();
      const { data } = await axios.get(`${API}/payments/saved-cards`, { 
        withCredentials: true, 
        headers 
      });
      setCards(data.cards || []);
    } catch (err) {
      console.error('Error fetching cards:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCards();
  }, []);

  const handleSetDefault = async (cardId) => {
    setSettingDefault(cardId);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/payments/set-default-card/${cardId}`, {}, { 
        withCredentials: true, 
        headers 
      });
      toast.success('Default card updated');
      fetchCards();
    } catch (err) {
      toast.error('Failed to update default card');
    } finally {
      setSettingDefault(null);
    }
  };

  const handleDeleteCard = async (cardId) => {
    setDeletingCard(cardId);
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/payments/saved-cards/${cardId}`, { 
        withCredentials: true, 
        headers 
      });
      toast.success('Card removed');
      fetchCards();
    } catch (err) {
      toast.error('Failed to remove card');
    } finally {
      setDeletingCard(null);
    }
  };

  const handleCardAdded = () => {
    setShowAddCard(false);
    fetchCards();
  };

  const getCardStyle = (brand) => {
    return cardBrandStyles[brand?.toLowerCase()] || cardBrandStyles.default;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-[#5ebc67]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Saved Cards List */}
      {cards.length > 0 ? (
        <div className="space-y-3">
          {cards.map((card) => {
            const style = getCardStyle(card.brand);
            return (
              <div
                key={card.id}
                className={`flex items-center justify-between p-4 rounded-xl border ${style.border} ${style.bg}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${style.bg}`}>
                    <CreditCard className={`w-5 h-5 ${style.text}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900 capitalize">{card.brand}</span>
                      <span className="text-slate-600">•••• {card.last4}</span>
                      {card.is_default && (
                        <Badge className="bg-[#5ebc67] text-white text-xs">
                          <Star className="w-3 h-3 mr-1" /> Default
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">
                      Expires {card.exp_month.toString().padStart(2, '0')}/{card.exp_year}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {!card.is_default && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSetDefault(card.id)}
                      disabled={settingDefault === card.id}
                      className="text-slate-600 hover:text-[#5ebc67]"
                      data-testid={`set-default-card-${card.last4}`}
                    >
                      {settingDefault === card.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <><Check className="w-4 h-4 mr-1" /> Set Default</>
                      )}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteCard(card.id)}
                    disabled={deletingCard === card.id}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-card-${card.last4}`}
                  >
                    {deletingCard === card.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-200">
          <CreditCard className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600 mb-1">No saved cards</p>
          <p className="text-sm text-slate-500">Add a card for faster checkout</p>
        </div>
      )}

      {/* Add Card Section */}
      {showAddCard ? (
        <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
          <h4 className="font-medium text-slate-900 mb-4">Add New Card</h4>
          <AddCardForm 
            onSuccess={handleCardAdded} 
            onCancel={() => setShowAddCard(false)} 
          />
        </div>
      ) : (
        <Button
          variant="outline"
          onClick={() => setShowAddCard(true)}
          className="w-full rounded-full border-dashed"
          data-testid="add-new-card-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New Card
        </Button>
      )}
    </div>
  );
};

// Wrapper with Stripe Provider
const SavedCards = () => {
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-[#5ebc67]" />
          Saved Payment Methods
        </CardTitle>
        <CardDescription>
          Save your cards for faster checkout when booking parking spots.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Elements stripe={stripePromise}>
          <SavedCardsContent />
        </Elements>
      </CardContent>
    </Card>
  );
};

export default SavedCards;
